import { Icon } from "armstrong-react";
import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Loader } from "../../../../core/atoms/atoms";
import { CardContainer } from "../../../../shared/components/inspiration/cardContainer";
import { FlexiCard } from "../../../../shared/components/inspiration/cards";
import { apiPromiseHook, createEnterHookPromise } from "../../../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../../../shared/helpers/contentCollectionHelpers";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import ResponsiveContainer from "../../../../shared/helpers/responsiveContainer";
import { StoreActionFactory } from "../../../../shared/state/storeActionFactory";
import { SwipeCarousel } from "../../../components/swipeCarousel/swipeCarousel";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { ActionPromises } from "../../../state/actionPromises";

import "./tripIdeas.scss";

const EnquiryUpsellCard = (props: { country: ICountry }) => {
  return (
    <FlexiCard
      to={WebRoutes.enquiryForm({ locationSlug: props.country.slug, campaign: "country-trip-ideas" })}
      className="send-enquiry cta-card"
      icomoon={Icon.Icomoon.paperplane}
      title="Something in mind?"
      description={`If you already have an idea of your dream holiday in ${props.country.name},`
        + "send an enquiry and we'll design your perfect tailor-made holiday!"}
      cta="Plan your bespoke holiday"
    />
  );
}

const TripIdeaCards = (params: { country: ICountry, groupTours: boolean }) => {
  return (
    <ResponsiveContainer
      mobile={() => (
        <CardContainer
          collectionKey={
            params.groupTours ? CollectionKeyHelper.LocationItinerariesGroupTours(params.country.id) : CollectionKeyHelper.LocationItinerariesTailoredTours(params.country.id)
          }
          filters={{
            locations: [params.country.id],
            types: [ContentType.SampleItinerary],
          }}
          renderCardWrapper={elements => (
            <SwipeCarousel>
              {elements}
              {!params.groupTours && <EnquiryUpsellCard country={params.country} />}
            </SwipeCarousel>
          )}
        />
      )}
      tablet={() => (
        <CardContainer
          collectionKey={
            params.groupTours ? CollectionKeyHelper.LocationItinerariesGroupTours(params.country.id) : CollectionKeyHelper.LocationItinerariesTailoredTours(params.country.id)
          }
          filters={{
            locations: [params.country.id],
            types: [ContentType.SampleItinerary],
          }}
          columnClassName="column is-6-tablet is-4-desktop is-3-widescreen is-3-fullhd"
        >
          {!params.groupTours && <EnquiryUpsellCard country={params.country} />}
        </CardContainer>
      )}
    />
  );
}

type Props = ReturnType<typeof stateToProps>;

export const TripIdeasComponent = ({ ltoState, countryState, contentState }: Props) => {
  const country = countryState.data as ICountry;
  if (countryState.status !== "OK") {
    return <Loader />;
  }

  const lto = ltoState.data;
  if (!lto) {
    const subject = countryState.data ? `Enquiry about ${countryState.data.name}` : "Enquiry";
    const mailtoLink = `mailto:team@travellocal.com?subject=${subject}`;
    return (
      <div className="p-top-large p-bottom-large">
        <div className="width-wrapper">
          <h1>Coming soon</h1>
          <p>If you're thinking of planning a trip here, send us an email at <a href={mailtoLink}>team@travellocal.com</a>, and we'd be happy to help you further!</p>
        </div>
      </div>
    );
  }

  const groupItinerariesState = contentState.data[CollectionKeyHelper.LocationItinerariesGroupTours(country.id)];

  return (
    <div className="country-trip-ideas">
      <Helmet title={country.ltoPageTitleTag}
        meta={[
          { name: "description", content: country.ltoPageDescriptionTag },
        ]}
      />

      <section className="tailor-made card-section">
        <div className="width-wrapper">
          <h2>Tailor-made holidays</h2>
          <h3>{countryState.data.name} trip ideas that can be tailored to meet your needs and interests.</h3>

          <TripIdeaCards country={country} groupTours={false} />
        </div>
      </section>

      {MatchState(groupItinerariesState, {
        data: groupItineraries => groupItineraries.length > 0 && (
          <section className="group-tours card-section">
            <div className="width-wrapper">
              <h2>Group tours</h2>
              <h3>{countryState.data.name} trips with fixed itineraries and scheduled start dates.</h3>

              <TripIdeaCards country={country} groupTours={true} />
            </div>
          </section>
        ),
      })}
    </div>
  );
}

const stateToProps = (state: IWebStoreState) => ({
  countryState: state.country,
  ltoState: state.lto, // TODO Ignore all non-default LTOs until this page is redesigned
  contentState: state.contentByCollection,
  currency: state.currency,
});

export const TripIdeas = connect(stateToProps)(TripIdeasComponent);

export const tripIdeasOnEnter = apiPromiseHook<any>(
  createEnterHookPromise((router) => {
    const countrySlug = router.params.countrySlug;

    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      const { country, lto } = getState();

      if (!country
        || !lto.data
        || !country.data
        || country.data.slug !== countrySlug
        || (country.data.defaultLto && lto.data.id !== country.data.defaultLto.id)) {

        await dispatch(ActionPromises.countryAndDefaultLtoFromSlug(countrySlug));
      }

      const updatedCountry = getState().country.data;

      if (updatedCountry) {
        return dispatch(ActionPromises.getItinerariesForCountryTripIdeas({
          locations: [updatedCountry.id],
        }));
      }
    });
  }),
);
